import { useCallback, useState } from "react";
import { DocumentData } from "../../components/documents/DocumentCollector/types";
import useGuestDocumentDelete from "./useGuestDocumentDelete";
import useGuestDocumentUpload from "./useGuestDocumentUpload";

/**
 * Orchestrates document management operations (upload, delete, metadata update)
 * by coordinating specialized hooks for each operation type.
 *
 * @param caseId - Case ID for document operations
 * @param profileId - Profile ID for document association
 * @returns { processDocuments, loading } - Function for processing documents and loading state
 */
export default function useGuestDocumentProcessor(emailToken: string) {
  const [isProcessing, setIsProcessing] = useState(false);

  const { deleteGuestDocuments, loading: deleteLoading } = useGuestDocumentDelete();
  const { uploadDocuments, loading: uploadLoading } = useGuestDocumentUpload(emailToken);
  /**
   * Main orchestration function that:
   * 1. Identifies files to delete and upload
   * 2. Deletes replaced documents
   * 3. Uploads new documents
   *
   * @param newSelectedFiles - Form values with current document selections
   * @param initialFiles - Original documents for comparison
   * @returns Promise resolving to updated documents
   */
  const processDocuments = useCallback(
    async (newFiles: DocumentData[], deleteDocIds: number[]) => {
      setIsProcessing(true);
      try {
        // First delete documents that have been replaced
        await deleteGuestDocuments(deleteDocIds, emailToken);

        // Then upload and register new documents
        const updatedDocuments = await uploadDocuments(newFiles);

        return updatedDocuments;
      } finally {
        setIsProcessing(false);
      }
    },
    [emailToken, deleteGuestDocuments, uploadDocuments],
  );

  return {
    processDocuments,
    loading: isProcessing || deleteLoading || uploadLoading,
  };
}
